import { Component, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import * as braintree from 'braintree-web';
import { loadScript, PayPalButtonsComponent } from "@paypal/paypal-js";
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PaymentsService } from '../../data-access/payments.service';
import { PaypalClientSelectorService } from '../../data-access/paypalClientSelector.service';
import { BraintreeService } from '../../data-access/braintree.service';
import { OrderSummaryComponent } from '../../ui/order-summary/order-summary.component';
import { CartStateService } from '../../data-access/cart-state.service';
import { UPDATE_USER } from '@equine-ui-nx/injection-tokens';
import { ErrorDialogComponent } from '../../ui/error-dialog/error-dialog.component';


@Component({
  standalone: true,
  selector: 'equine-ui-nx-pay-with-bt-paypal',
  imports: [
    MatCardModule,
    MatProgressSpinnerModule,
    OrderSummaryComponent
  ],
  providers: [
    PaymentsService,
    MatSnackBar
  ],
  templateUrl: './pay-with-bt-paypal.component.html',
  styleUrl: './pay-with-bt-paypal.component.scss',
})
export class PayWithBtPaypalComponent implements OnInit, OnDestroy {

  private paymentsService = inject(PaymentsService);
  private router = inject(Router);
  private braintreeService = inject(BraintreeService);

  cartReference = this.paymentsService.getLoggedInUserId() || '';
  cartIntent = 'CAPTURE';
  private paypalClientSelectorService = inject(PaypalClientSelectorService);
  private paypalClient!: string;
  
  public paypalButtons!: PayPalButtonsComponent;
  private _snackBar = inject(MatSnackBar);
  private _dialog = inject(MatDialog);
  private cartState = inject(CartStateService);

  private clientInstance!: braintree.Client;
  private dataCollectorInstance!: braintree.DataCollector;

  orders = this.cartState.ordersForSum;
  subTotal = this.cartState.subtotal;
  totalItems = this.cartState.totalItems;
  donationOrder = this.cartState.donationOrder;
  donationOrderTotal = this.cartState.donationOrderTotal;
  total = this.cartState.total;
  coupon = this.cartState.coupon;

  loading = signal(true);
  paypalLoading = signal(true);
  cartCompleted = signal(false);
  cartError = signal(null);

  constructor(
    @Inject(UPDATE_USER) private updateUserToken: BehaviorSubject<void>
  ) {}

  ngOnInit() {
    this.getAmount();
  }

  getAmount() {
    this.loading.set(true);
    
    this.paymentsService.getCartSummary(undefined, this.cartReference)
      .subscribe({
        next: (response) => {
          this.cartIntent = response.cart?.intent || 'CAPTURE';
          this.paypalClient = this.paypalClientSelectorService.getPaypalClientId(response.paypalClient);

          this.loadPaypalButton();
          this.loading.set(false);
          if (response.status === 'COMPLETE') {
            this.cartCompleted.set(true);
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'An error ocurred. Cannot load cart'
          this.cartError.set(errorMessage);
          this.loading.set(false);
        }
      });
  }


  loadPaypalButton() {
    this.braintreeService.getBraintreeTokenAndCustomerData(this.cartReference)
    .subscribe({
      next: (response: {clientToken: string}) => {
        this.getBraintreeInstance(response.clientToken);
        debugger
      },
      error: () => {
        void 0;
      }
    });
  }

  getBraintreeInstance(clientToken: string) {
    braintree.client.create({authorization: clientToken})
      .then((clientInstance: braintree.Client) => {

        this.clientInstance = clientInstance;

        this.setDataCollector();
        this.createPaypalInstance();
      });
  }

  setDataCollector() {
    braintree.dataCollector.create({
      client: this.clientInstance
    }).then((dataCollectorInstance: braintree.DataCollector) => {
      this.dataCollectorInstance = dataCollectorInstance;
    })
    .catch(() => {
      void 0;
    }); 
  }

  createPaypalInstance() {
    braintree.paypalCheckout.create({
      client: this.clientInstance
    })
    .then((paypalCheckoutInstance: braintree.PayPalCheckout) => {
      // Load the PayPal JS SDK (see Load the PayPal JS SDK section)
      const loadPayPalSDKOptions: any = {
        currency: 'USD', // Must match the currency passed in with createPayment
        intent: 'capture', // Must match the intent passed in with createPayment
      }
      paypalCheckoutInstance.loadPayPalSDK(loadPayPalSDKOptions)
        .then((paypal: braintree.PayPalCheckout) => {
          this.loading.set(false);
          this.createPaypalButton(paypal);
        }) 
      })
  }

  createPaypalButton(paypalInstance: braintree.PayPalCheckout) {
    loadScript({
      clientId: this.paypalClient,
      components: ['buttons'],
      intent: 'capture',
      vault: true
    }).then((paypal) => {
      this.paypalLoading.set(false);

      if (!paypal) {
        return;
      } else {
        this.paypalButtons = (paypal as any).Buttons({
          style: {
            layout:  'horizontal',
            color:   'gold',
            shape:   'rect',
            label:   'pay',
            tagline: true
          },
          createOrder: () => {
            return new Promise((resolve, reject) => {
              this.braintreeService.getBraintreePaymentOptionsPayloadForPaypal(
                this.cartReference
              ).subscribe({
                next: (createPaymentRequestOptions: {amount: string, payload: any}) => {
                  resolve(paypalInstance.createPayment(createPaymentRequestOptions.payload))
                },
                error: (error) => {
                  reject(error)
                }
              });
            });
          },
          onApprove: (orderData: any) => {
            return new Promise((resolve, reject) => {
              paypalInstance.tokenizePayment(orderData).then((payload: paypal.TokenizePayload) => {

                console.log(payload);
                this.braintreeService.payWithPaypal(
                  this.cartState.cartId() as string,
                  payload.nonce,
                  this.dataCollectorInstance.deviceData,
                ).subscribe({
                  next: (success) => {
                    this.paymentsService.setCartAsEmptyAfterPayment();
                    this.fetchUserIfNeeded(success);
                    this._snackBar.open(`Success!`, 'Dismiss', {
                      duration: 5000,
                      panelClass: ['success-snackbar'],
                    });
                    this.router.navigate(['payments', 'success']);

                    resolve(success);
                  },
                  error: (error) => {
                    reject(error);
                  }
                });
              })
              .catch((error) => {
                reject(error);
              });
            });
          },
          onError: (error: any) => {
            if (error?.error?.message || error?.message) {
              this._dialog.open(ErrorDialogComponent, {
                data: {
                  message: error?.error?.message || error.message || `An error occured. Please try again`
                }
              });
            } else {
              this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
            }
          },
        });

        this.paypalButtons?.render('#paypalButtons');
      }
    });
  }

  fetchUserIfNeeded(successResponse: any) {
    // if(successResponse?.orders?.some((each: any)=> {
    //   each?.afterPaymentKey === 'MEMBERSHIP'
    // })) {
      this.updateUserToken.next();
    // }
  }

  goBack() {
    this.router.navigate(['payments', 'payment-summary'])
  }

  ngOnDestroy(): void {
    void 0;
  }

}
